import { useState, useCallback } from 'react'
import { notify } from '../stores/notificationStore'

interface UseCopyToClipboardOptions {
  showNotification?: boolean
  successMessage?: string
  errorMessage?: string
}

export function useCopyToClipboard(options: UseCopyToClipboardOptions = {}) {
  const {
    showNotification = true,
    successMessage = 'Copied to clipboard',
    errorMessage = 'Failed to copy',
  } = options

  const [copiedText, setCopiedText] = useState<string | null>(null)
  const [isCopying, setIsCopying] = useState(false)

  const copy = useCallback(
    async (text: string, label?: string): Promise<boolean> => {
      if (!navigator?.clipboard) {
        if (showNotification) {
          notify.error('Clipboard unavailable', 'Your browser does not support clipboard access')
        }
        return false
      }

      setIsCopying(true)
      try {
        await navigator.clipboard.writeText(text)
        setCopiedText(text)

        if (showNotification) {
          notify.success(
            label ? `${label} copied` : successMessage,
            undefined,
            { duration: 2000 }
          )
        }

        // Reset after 2 seconds
        setTimeout(() => setCopiedText(null), 2000)
        return true
      } catch (error) {
        console.error('Failed to copy:', error)
        if (showNotification) {
          notify.error(errorMessage)
        }
        return false
      } finally {
        setIsCopying(false)
      }
    },
    [showNotification, successMessage, errorMessage]
  )

  return { copy, copiedText, isCopying }
}
